import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase, fmtError } from "@/lib/supabase";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, ShieldCheck } from "lucide-react";

/**
 * /reset-password
 *
 * Step 2 of the Supabase password-reset flow.
 *
 *   • Supabase verifies the email link, creates an authenticated session,
 *     then redirects the browser here (via the `redirectTo` we passed at
 *     step 1). We rely on `getSession()` to confirm the session exists.
 *   • The deprecated `PASSWORD_RECOVERY` event isn't fired reliably in
 *     supabase-js v2 web flows — `SIGNED_IN` is, so we also listen for
 *     that to cover the rare race where the session lands a tick late.
 *   • Submitting calls `supabase.auth.updateUser({ password })`. The
 *     current recovery session is sufficient — no current-password
 *     re-prompt needed for the reset flow.
 *
 * After success we sign the user OUT and bounce to /login so they re-enter
 * their NEW password — clean separation from the recovery session, and
 * matches user expectations of "I reset → now I log in".
 */

function scorePassword(pwd) {
  if (pwd.length < 8) return 0; // "Too short" — gate everything on minimum length.
  let s = 1;
  if (pwd.length >= 12) s++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) s++;
  if (/\d/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  return Math.min(s, 4); // 0..4
}
const SCORE_LABEL = ["Too short", "Weak", "OK", "Good", "Strong"];
const SCORE_COLOR = ["bg-destructive", "bg-destructive", "bg-amber-400", "bg-lime-400", "bg-lime-500"];

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [loadingSession, setLoadingSession] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Detect the recovery session created by clicking the email link.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data, error: e }) => {
      if (cancelled) return;
      if (!e && data?.session) setSessionReady(true);
      setLoadingSession(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if ((event === "SIGNED_IN" || event === "PASSWORD_RECOVERY") && session) {
        setSessionReady(true);
        setLoadingSession(false);
      }
    });
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  const score = useMemo(() => scorePassword(pwd), [pwd]);
  const matches = pwd && pwd === pwd2;
  const canSubmit = sessionReady && pwd.length >= 8 && matches && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pwd !== pwd2) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password: pwd });
      if (err) {
        setError(fmtError(err));
        return;
      }
      toast.success("Password updated. Log in with your new password.");
      // Drop the recovery session and bounce to login for a clean re-auth.
      await supabase.auth.signOut();
      navigate("/login", { replace: true });
    } catch (err) {
      setError(fmtError(err) || "Couldn't update password. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingSession) {
    return (
      <div className="bg-paper min-h-[calc(100vh-4rem)] flex items-center justify-center px-6">
        <div className="text-xs uppercase tracking-[0.3em] text-ink/55 animate-pulse">
          Preparing reset…
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-ink/15 bg-grain">
        <Link to="/" className="inline-flex items-center">
          <img src="/inonit-logo.png" alt="inonit" className="h-9 w-auto select-none" draggable={false} />
        </Link>
        <div>
          <p className="font-display text-4xl xl:text-6xl font-bold leading-[0.95] tracking-tight text-ink">
            New password.<br />Fresh start.
          </p>
          <p className="text-ink/60 mt-6 max-w-md">
            Pick something strong. Eight characters minimum, mix it up.
          </p>
        </div>
        <span className="text-xs uppercase tracking-widest text-ink/40">© inonit</span>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md" data-testid="reset-password-card">
          {!sessionReady ? (
            <InvalidLinkState />
          ) : (
            <>
              <span className="text-xs uppercase tracking-[0.3em] text-ink">Choose a new password</span>
              <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 mb-10 leading-none">
                Almost back<br />in.
              </h1>

              <form onSubmit={submit} data-testid="reset-password-form" className="flex flex-col gap-6">
                <label className="flex flex-col gap-2">
                  <span className="text-xs uppercase tracking-widest text-ink/60">New password</span>
                  <div className="relative">
                    <input
                      type={showPwd ? "text" : "password"}
                      required
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      data-testid="reset-password-new"
                      placeholder="••••••••"
                      className="chunky-input pr-12"
                      autoComplete="new-password"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      data-testid="reset-password-toggle-visibility"
                      className="absolute inset-y-0 right-3 inline-flex items-center justify-center text-ink/55 hover:text-ink"
                    >
                      {showPwd ? <EyeOff size={18} strokeWidth={2.4} /> : <Eye size={18} strokeWidth={2.4} />}
                    </button>
                  </div>
                  {pwd && (
                    <div className="flex items-center gap-2 mt-1" data-testid="reset-password-strength">
                      <div className="h-1.5 flex-1 rounded-full bg-ink/10 overflow-hidden">
                        <div
                          className={`h-full ${SCORE_COLOR[score]} transition-all`}
                          style={{ width: `${(score / 4) * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-ink/55 w-16 text-right">
                        {SCORE_LABEL[score]}
                      </span>
                    </div>
                  )}
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-xs uppercase tracking-widest text-ink/60">Confirm password</span>
                  <input
                    type={showPwd ? "text" : "password"}
                    required
                    value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    data-testid="reset-password-confirm"
                    placeholder="••••••••"
                    className="chunky-input"
                    autoComplete="new-password"
                  />
                  {pwd2 && !matches && (
                    <span data-testid="reset-password-mismatch" className="text-[11px] text-destructive uppercase tracking-widest">
                      Passwords don't match.
                    </span>
                  )}
                </label>

                {error && (
                  <div data-testid="reset-password-error" className="text-destructive text-sm border border-destructive/40 px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="reset-password-submit"
                  className="bg-neon text-ink py-4 font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60 mt-2 inline-flex items-center justify-center gap-2"
                >
                  <ShieldCheck size={16} strokeWidth={2.6} />
                  {submitting ? "Updating…" : "Update password"}
                </button>

                <Link
                  to="/login"
                  data-testid="reset-back-to-login"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60 hover:text-ink transition-colors self-start"
                >
                  <ArrowLeft size={14} strokeWidth={2.6} /> Back to log in
                </Link>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InvalidLinkState() {
  return (
    <div data-testid="reset-password-invalid">
      <span className="text-xs uppercase tracking-[0.3em] text-ink">Hmm…</span>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 mb-6 leading-none">
        This link is<br />no longer valid.
      </h1>
      <p className="text-ink/70 text-sm mb-8 max-w-md leading-relaxed">
        Reset links expire after one hour and can only be used once.
        Request a fresh one from the forgot-password page.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Link
          to="/forgot-password"
          data-testid="reset-link-request-new"
          className="bg-neon text-ink py-3 px-5 font-bold uppercase tracking-widest text-sm text-center hover:bg-paper border-[2.5px] border-ink transition-colors"
        >
          Get a new link
        </Link>
        <Link
          to="/login"
          data-testid="reset-back-to-login-invalid"
          className="py-3 px-5 font-bold uppercase tracking-widest text-sm text-center text-ink/60 hover:text-ink transition-colors"
        >
          Back to log in
        </Link>
      </div>
    </div>
  );
}
